import type { SatelliteWebLandlordGetLatestForContactsPageRequestDto } from '@zg-rentals/ts-satellite-client';
import { useContext, useMemo } from 'react';
import { MultiFamilyLeadsPageContext } from '../contexts/MultiFamilyLeadsContextProvider';
import { getContactsPageConversationsQueryParams } from '../utils/contactsPageConversationsQueryParams';
import { getDateRangesInMsFromDaysBackOrStartDate } from '../utils/timeFrameDropdownUtils';
import { DEFAULT_DAYS_BACK } from '../components/TimeFrameDropdown';

/**
 * Returns the query params for getLatestConversationsForContactsPage using
 * MultiFamilyLeadsPageContext and the given pagination. Use this when updating
 * the contacts page React Query cache (e.g. after sending a message or saving a note)
 * so the cache key matches LeadsTable's query.
 * Returns null when not inside MultiFamilyLeadsPageContext (e.g. on inbox or lead management).
 */
export function useContactsPageConversationsQueryParams(
  limit: number,
  start: number,
): SatelliteWebLandlordGetLatestForContactsPageRequestDto | null {
  const { clientTimeZone, daysBack, listingAlias, startDate } = useContext(MultiFamilyLeadsPageContext);

  return useMemo(() => {
    if (!clientTimeZone) {
      return null;
    }
    const daysBackArg = daysBack ? parseInt(daysBack, 10) : DEFAULT_DAYS_BACK;
    const { startDateMs, endDateMs } = getDateRangesInMsFromDaysBackOrStartDate(daysBackArg, startDate);
    return getContactsPageConversationsQueryParams({
      clientTimeZone,
      startDateMs,
      endDateMs,
      limit,
      start,
      listingAlias,
    });
  }, [clientTimeZone, daysBack, startDate, listingAlias, limit, start]);
}
